import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  Stack,
  FormControlLabel,
  Switch
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';

const DEFAULT_SMTP_SETTINGS = {
  enabled: false,
  host: '',
  port: '587',
  secure: false,
  username: '',
  password: '',
  testEmail: ''
};

const SmtpSettings = () => {
  const [settings, setSettings] = useState(DEFAULT_SMTP_SETTINGS);
  const [status, setStatus] = useState({ type: '', message: '' });
  const [isTesting, setIsTesting] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      // Utiliser la même logique de détection d'URL que le service API centralisé
      const API_BASE_URL = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' 
        ? 'http://localhost:3002'
        : (window.location.hostname.match(/^192\.168\.|^10\.|^172\.(1[6-9]|2[0-9]|3[0-1])\./) 
          ? `${window.location.protocol}//${window.location.hostname}:3002`
          : 'https://api.astreintes.provserver.ovh');

      const response = await fetch(`${API_BASE_URL}/api/global/smtp-settings`);
      if (response.ok) {
        const smtpSettings = await response.json();
        setSettings({
          ...smtpSettings,
          testEmail: settings.testEmail // Conserver l'email de test local
        });
      }
    } catch (error) {
      console.error('Erreur chargement paramètres SMTP:', error);
      // Fallback sur localStorage pour rétro-compatibilité
      const savedSettings = localStorage.getItem('smtpSettings');
      if (savedSettings) {
        setSettings(JSON.parse(savedSettings));
      }
    }
  };

  const handleChange = (e) => {
    const { name, value, checked } = e.target;
    setSettings(prev => ({
      ...prev,
      [name]: e.target.type === 'checkbox' ? checked : value
    }));
  };

  const handleSave = async () => {
    try {
      // Utiliser la même logique de détection d'URL que le service API centralisé
      const API_BASE_URL = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' 
        ? 'http://localhost:3002'
        : (window.location.hostname.match(/^192\.168\.|^10\.|^172\.(1[6-9]|2[0-9]|3[0-1])\./) 
          ? `${window.location.protocol}//${window.location.hostname}:3002`
          : 'https://api.astreintes.provserver.ovh');

      const normalized = {
        enabled: Boolean(settings.enabled),
        host: settings.host?.trim() || '',
        port: parseInt(settings.port) || 587,
        secure: Boolean(settings.secure),
        username: settings.username?.trim() || '',
        password: settings.password || ''
      };

      const response = await fetch(`${API_BASE_URL}/api/global/smtp-settings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(normalized)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Erreur lors de la sauvegarde');
      }

      // Sauvegarder aussi en localStorage pour les anciennes fonctionnalités qui en dépendent encore
      localStorage.setItem('smtpSettings', JSON.stringify(normalized));

      setStatus({
        type: 'success',
        message: 'Configuration SMTP enregistrée avec succès sur le serveur'
      });
      setTimeout(() => setStatus({ type: '', message: '' }), 3000);
    } catch (error) {
      console.error('Erreur sauvegarde SMTP:', error);
      setStatus({
        type: 'error',
        message: error.message || 'Erreur lors de l\'enregistrement de la configuration'
      });
    }
  };

  const handleTest = async () => {
    setIsTesting(true);
    try {
      // Utiliser la même logique de détection d'URL que le service API centralisé
      const API_BASE_URL = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' 
        ? 'http://localhost:3002'
        : (window.location.hostname.match(/^192\.168\.|^10\.|^172\.(1[6-9]|2[0-9]|3[0-1])\./) 
          ? `${window.location.protocol}//${window.location.hostname}:3002`
          : 'https://api.astreintes.provserver.ovh');

      const response = await fetch(`${API_BASE_URL}/api/send-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          to: settings.testEmail,
          from: `Test SMTP <${settings.username}>`,
          subject: 'Test de configuration SMTP',
          body: 'Si vous recevez cet email, la configuration SMTP est fonctionnelle.',
          smtp: {
            host: settings.host,
            port: parseInt(settings.port),
            secure: settings.secure,
            auth: {
              user: settings.username,
              pass: settings.password
            }
          }
        })
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Erreur lors de l\'envoi du mail de test');
      }

      setStatus({
        type: 'success',
        message: 'Email de test envoyé avec succès'
      });
    } catch (error) {
      setStatus({
        type: 'error',
        message: 'Erreur lors de l\'envoi du mail de test: ' + error.message
      });
    } finally {
      setIsTesting(false);
    }
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Stack spacing={3}>
        <Typography variant="h6">Configuration du serveur SMTP</Typography>

        <FormControlLabel
          control={
            <Switch
              checked={settings.enabled}
              onChange={handleChange}
              name="enabled"
            />
          }
          label="Activer l'envoi d'emails via SMTP"
        />

        <TextField
          label="Hôte SMTP"
          name="host"
          value={settings.host}
          onChange={handleChange}
          fullWidth
          disabled={!settings.enabled}
          placeholder="ex: smtp.gmail.com"
        />

        <TextField
          label="Port"
          name="port"
          value={settings.port}
          onChange={handleChange}
          fullWidth
          disabled={!settings.enabled}
        />

        <FormControlLabel
          control={
            <Switch
              checked={settings.secure}
              onChange={handleChange}
              name="secure"
              disabled={!settings.enabled}
            />
          }
          label="Utiliser une connexion sécurisée (SSL/TLS)"
        />

        <TextField
          label="Nom d'utilisateur"
          name="username"
          value={settings.username}
          onChange={handleChange}
          fullWidth
          disabled={!settings.enabled}
        />

        <TextField
          label="Mot de passe"
          name="password"
          type="password"
          value={settings.password}
          onChange={handleChange}
          fullWidth
          disabled={!settings.enabled}
        />

        <Box sx={{ borderTop: 1, borderColor: 'divider', pt: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            Test de la configuration
          </Typography>
          
          <Stack direction="row" spacing={2} alignItems="center">
            <TextField
              label="Email de test"
              name="testEmail"
              type="email"
              value={settings.testEmail}
              onChange={handleChange}
              sx={{ flexGrow: 1 }}
              disabled={!settings.enabled}
            />
            <Button
              variant="contained"
              startIcon={<SendIcon />}
              onClick={handleTest}
              disabled={!settings.enabled || !settings.testEmail || isTesting}
            >
              {isTesting ? 'Envoi...' : 'Tester'}
            </Button>
          </Stack>
        </Box>

        {status.message && (
          <Alert severity={status.type}>
            {status.message}
          </Alert>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!settings.enabled}
          >
            Enregistrer
          </Button>
        </Box>
      </Stack>
    </Paper>
  );
};

export default SmtpSettings; 